import dayjs from 'dayjs'
import isEqual from 'lodash.isequal'
import {
  EventDoc,
  EventRecurrence,
  EventStatus,
  Exclusion
} from 'types/api/changes'

import { recurrenceExclusionDate } from './recurrenceExclusions'

// Source wall fields are evidence for this import only, never persisted or
// uploaded. Weak keys survive the event's shallow copy in the Google mapper.
const googleExdateSources = new WeakMap<
  Exclusion,
  { wallTime: string; date: string; text: string }
>()

export function recordGoogleExdateSource(
  exclusion: Exclusion,
  wallTime: string
) {
  googleExdateSources.set(exclusion, {
    wallTime,
    date: exclusion.Date,
    text: exclusion.ExclusionDate
  })
}

function isLegacyExdateEncoding(old: Exclusion, fresh: Exclusion): boolean {
  const text = old.ExclusionDate
  const source = googleExdateSources.get(fresh)
  if (
    source &&
    (source.date !== fresh.Date || source.text !== fresh.ExclusionDate)
  )
    return false
  if (
    typeof text !== 'string' ||
    text !== (source?.wallTime ?? fresh.ExclusionDate) ||
    !/^\d{8}T\d{6}$/.test(text)
  )
    return false
  const wall = dayjs.utc(
    text.replace(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/,
      '$1-$2-$3T$4:$5:$6Z'
    )
  )
  if (!wall.isValid() || wall.format('YYYYMMDDTHHmmss') !== text) return false
  if (!source && wall.startOf('day').unix().toString() !== fresh.Date)
    return false
  const offset = wall.valueOf() - Number(old.Date) * 1000
  // The old importer stripped Z/ignored TZID and parsed these source wall
  // fields in the computer zone. Confirm that encoding with a possible offset.
  // The fresh exclusion already contains the event-zone canonical date; its
  // wall fields need not equal UTC or another source zone's wall fields.
  return Math.abs(offset) <= 14 * 60 * 60 * 1000 && offset % 60000 === 0
}

// Find a complete one-to-one assignment, then reject any alternating cycle:
// such a cycle permits another equally supported assignment. Both passes are
// iterative so large provider lists cannot exhaust the JavaScript call stack.
function uniqueExclusionMatches(candidates: number[][]): number[] | undefined {
  const count = candidates.length
  const oldToFresh = Array<number>(count).fill(-1)
  const freshToOld = Array<number>(count).fill(-1)
  for (let root = 0; root < count; root++) {
    const parent = Array<number>(count).fill(-1)
    const queue = [root]
    let available = -1
    for (let head = 0; head < queue.length && available < 0; head++) {
      const old = queue[head]
      for (const fresh of candidates[old]) {
        if (parent[fresh] >= 0) continue
        parent[fresh] = old
        if (freshToOld[fresh] < 0) {
          available = fresh
          break
        }
        queue.push(freshToOld[fresh])
      }
    }
    if (available < 0) return undefined
    while (available >= 0) {
      const old = parent[available]
      const previous = oldToFresh[old]
      oldToFresh[old] = available
      freshToOld[available] = old
      available = previous
    }
  }
  const edges = candidates.map((matches, old) =>
    matches.map((fresh) => freshToOld[fresh]).filter((other) => other !== old)
  )
  const incoming = Array<number>(count).fill(0)
  for (const list of edges) for (const other of list) incoming[other]++
  const queue = incoming.flatMap((degree, old) => (degree === 0 ? [old] : []))
  for (let head = 0; head < queue.length; head++) {
    for (const other of edges[queue[head]]) {
      if (--incoming[other] === 0) queue.push(other)
    }
  }
  return queue.length === count ? freshToOld : undefined
}

export function isUnchangedReadonlyGoogleEvent(
  existing: EventDoc | undefined,
  incoming: EventDoc | undefined
): existing is EventDoc {
  return !!(
    existing &&
    incoming &&
    existing.PersonalOwnerId === 'readonly' &&
    incoming.PersonalOwnerId === 'readonly' &&
    existing.local_id &&
    existing.local_id === incoming.local_id &&
    existing.ThirdPartyID &&
    existing.ThirdPartyID === incoming.ThirdPartyID &&
    existing.ThirdPartyLastUpdate &&
    Number.isFinite(Number(existing.ThirdPartyLastUpdate)) &&
    existing.ThirdPartyLastUpdate === incoming.ThirdPartyLastUpdate
  )
}

export function googleRecurrenceDateRepair(
  existing: EventDoc,
  incoming: EventDoc
): EventRecurrence | undefined {
  if (!isUnchangedReadonlyGoogleEvent(existing, incoming)) return undefined
  if (
    ['StartDate', 'EndDate', 'AllDay', 'TimeZoneNameID'].some(
      (key) => existing[key] !== incoming[key]
    )
  )
    return undefined
  const saved = existing.Recurrence
  const fresh = incoming.Recurrence
  if (
    !saved ||
    !fresh ||
    typeof saved !== 'object' ||
    typeof fresh !== 'object'
  )
    return undefined
  const { Exclusion: oldDates, ...oldRule } = saved
  const { Exclusion: newDates, ...newRule } = fresh
  if (
    !isEqual(oldRule, newRule) ||
    !Array.isArray(oldDates) ||
    !Array.isArray(newDates) ||
    oldDates.length !== newDates.length ||
    [...oldDates, ...newDates].some(
      (value) => !value || typeof value !== 'object'
    )
  )
    return undefined

  // Ordinary syncs (including records already repaired) need no candidate
  // graph or timezone work. This also handles identical duplicate exclusions.
  if (isEqual(oldDates, newDates)) return undefined

  const newDatesByValue = new Map<string, number[]>()
  newDates.forEach((next, index) => {
    const indexes = newDatesByValue.get(next.Date) ?? []
    indexes.push(index)
    newDatesByValue.set(next.Date, indexes)
  })
  const candidates = oldDates.map((old) => {
    // Exact matches already outrank every legacy interpretation. Index by date
    // so reordered unchanged series do not require all-pairs comparisons.
    const exact = (newDatesByValue.get(old.Date) ?? []).filter((index) =>
      isEqual(old, newDates[index])
    )
    if (exact.length > 0) return exact

    const { Date: oldDate, ExclusionDate: _oldText, ...oldFields } = old
    const instant = Number(oldDate) * 1000
    let normalized: ReturnType<typeof recurrenceExclusionDate> | undefined
    const scores = newDates.map((next) => {
      if (isEqual(old, next)) return 3
      if (
        old.Status !== EventStatus.DELETED ||
        next.Status !== EventStatus.DELETED
      )
        return 0
      const { Date: newDate, ExclusionDate: newText, ...newFields } = next
      if (
        !isEqual(oldFields, newFields) ||
        oldDate == null ||
        String(oldDate).trim() === ''
      )
        return 0
      if (
        !Number.isFinite(instant) ||
        !Number.isFinite(new Date(instant).getTime())
      )
        return 0
      // A saved instant has the same interpretation for every candidate.
      // Normalize lazily at most once per entry, rather than once per pair.
      normalized ??= recurrenceExclusionDate(instant, existing)
      if (
        (normalized.Date === newDate && normalized.ExclusionDate === newText) ||
        isLegacyExdateEncoding(old, next)
      )
        return 2
      return oldDate === newDate ? 1 : 0
    })
    // Exact copies take precedence over legacy evidence; confirmed encodings
    // take precedence over coincidental equal dates. Never consume an entry
    // greedily: every saved exclusion must have a unique supported counterpart.
    const strongest = scores.reduce<number>(
      (best, score) => Math.max(best, score),
      0
    )
    return scores.flatMap((score, index) =>
      score > 0 && score === strongest ? [index] : []
    )
  })
  const matches = uniqueExclusionMatches(candidates)
  if (!matches) return undefined
  let changed = false
  const repaired = newDates.map((next, fresh) => {
    const old = oldDates[matches[fresh]]
    changed ||=
      old.Date !== next.Date || old.ExclusionDate !== next.ExclusionDate
    return { ...old, Date: next.Date, ExclusionDate: next.ExclusionDate }
  })
  return changed ? { ...saved, Exclusion: repaired } : undefined
}
